import React, { useState } from "react";
import { observer } from "mobx-react";

import { deserializeHash } from "@formant/core/common/serializeHash";
import { Uuid } from "@formant/core/model/Uuid";

import { useRouter } from "../../context/RouterContext";
import { useStores } from "../../context/StoreContext";
import { Navigate } from "../common/Navigate";
import { NotFound } from "../NotFound";
import { OverviewViewName } from "../Overview/OverviewV2/OverviewV2";
import { ConnectedViewDevice } from "./ConnectedViewDevice";
import { ViewDeviceState } from "./ViewDeviceState";

interface IConnectedViewDeviceWithDefaultViewProps {
    id: Uuid;
}

function getCurrentHashParameters(): Record<string, any> {
    if (!window.location.hash) {
        return {};
    }
    try {
        return deserializeHash(window.location.hash.substring(1)) as Record<
            string,
            any
        >;
    } catch (error) {
        return {};
    }
}

export const ConnectedViewDeviceWithDefaultView = observer(
    function ConnectedViewDeviceWithDefaultView(
        props: IConnectedViewDeviceWithDefaultViewProps,
    ) {
        const { id: deviceId } = props;
        const [{ viewId: hashViewId }] = useState(() => new ViewDeviceState());
        const { routeTo } = useRouter();
        const { deviceDetailsStore, viewStore, observeStore } = useStores();

        const deviceDetails = deviceDetailsStore.get(deviceId);
        if (deviceDetails === false) {
            return <NotFound />;
        }

        // when visting new view while editing another
        // stop editing existing one
        observeStore.configuringAnalyticsId = undefined;
        observeStore.isCustomizing = false;

        const views = deviceDetails
            ? viewStore.listMatchingSingleDevice(deviceDetails.tags)
            : undefined;

        const isLoading = views === undefined;
        if (isLoading) {
            return null;
        }

        // avoid multi-device views with urls when clicking on a device.
        const firstView = views.find(
            _ =>
                !_.url || (_.showOnSingleDevice && _.name !== OverviewViewName),
        );
        // if we have viewId from hashObservable, use that
        if (
            hashViewId &&
            views.some(
                it => it.id === hashViewId && it.name !== OverviewViewName,
            )
        ) {
            const hashParameters = getCurrentHashParameters();
            return (
                <Navigate
                    replace={true}
                    to={routeTo.unifiedDevice(
                        {
                            mode: "views",
                            id: deviceId,
                            viewId: hashViewId,
                        },
                        hashParameters,
                    )}
                />
            );
        } else if (firstView) {
            if (firstView.id) {
                const hashParameters = getCurrentHashParameters();
                return (
                    <Navigate
                        replace={true}
                        to={routeTo.unifiedDevice(
                            {
                                mode: "views",
                                id: deviceId,
                                viewId: firstView.id,
                            },
                            hashParameters,
                        )}
                    />
                );
            }
        }

        return <ConnectedViewDevice id={deviceId} viewId={undefined} />;
    },
);
